import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmModal, Spinner } from "src/components";

type HistoryDownloadFeedbackModalKey = "contentEmptyAlert" | "downloadAlert" | "errorAlert";

export type HistoryDownloadFeedbackModalState = Record<HistoryDownloadFeedbackModalKey, { isOpen: boolean }>;

type HistoryDownloadFeedbackModalsProps = {
  emptyMessage: ReactNode;
  isDownloading: boolean;
  modal: HistoryDownloadFeedbackModalState;
  dateRequiredMessage?: ReactNode;
  downloadCompleteMessage?: ReactNode;
  downloadingMessage?: ReactNode;
  onCloseContentEmptyAlert: () => void;
  onCloseDownloadAlert: () => void;
  onCloseErrorAlert: () => void;
};

const INITIAL_MODAL_STATE: HistoryDownloadFeedbackModalState = {
  contentEmptyAlert: {
    isOpen: false,
  },
  downloadAlert: {
    isOpen: false,
  },
  errorAlert: {
    isOpen: false,
  },
};

export const useHistoryDownloadFeedbackModals = () => {
  const [modal, setModal] = useState<HistoryDownloadFeedbackModalState>(INITIAL_MODAL_STATE);

  const openModal = useCallback((key: HistoryDownloadFeedbackModalKey) => {
    setModal((prevModal) => ({ ...prevModal, [key]: { isOpen: true } }));
  }, []);

  const closeModal = useCallback((key: HistoryDownloadFeedbackModalKey) => {
    setModal((prevModal) => ({ ...prevModal, [key]: { isOpen: false } }));
  }, []);

  return {
    modal,
    openContentEmptyAlert: () => openModal("contentEmptyAlert"),
    closeContentEmptyAlert: () => closeModal("contentEmptyAlert"),
    openDownloadAlert: () => openModal("downloadAlert"),
    closeDownloadAlert: () => closeModal("downloadAlert"),
    openErrorAlert: () => openModal("errorAlert"),
    closeErrorAlert: () => closeModal("errorAlert"),
  };
};

const HistoryDownloadFeedbackModals = (props: HistoryDownloadFeedbackModalsProps) => {
  const { t } = useTranslation("common");
  const {
    emptyMessage,
    isDownloading,
    modal,
    dateRequiredMessage = <p>{t("selectPeriodRequired")}</p>,
    downloadCompleteMessage = <p>{t("downloadComplete")}</p>,
    downloadingMessage = <p>{t("downloading")}</p>,
    onCloseContentEmptyAlert,
    onCloseDownloadAlert,
    onCloseErrorAlert,
  } = props;

  return (
    <>
      {isDownloading && (
        <ConfirmModal isOpen={true}>
          <section className="contents-indicator">
            <Spinner size="page" />
          </section>
          <div className="font18 font-weight-600">{downloadingMessage}</div>
        </ConfirmModal>
      )}
      <ConfirmModal isOpen={modal.contentEmptyAlert.isOpen} btnRightTitle={t("confirm")} onClick={onCloseContentEmptyAlert}>
        <div className="font18 font-weight-600">{emptyMessage}</div>
      </ConfirmModal>
      <ConfirmModal isOpen={modal.downloadAlert.isOpen} btnRightTitle={t("confirm")} onClick={onCloseDownloadAlert}>
        <div className="font18 font-weight-600">{downloadCompleteMessage}</div>
      </ConfirmModal>
      <ConfirmModal isOpen={modal.errorAlert.isOpen} btnRightTitle={t("confirm")} onClick={onCloseErrorAlert}>
        <div className="font18 font-weight-600">{dateRequiredMessage}</div>
      </ConfirmModal>
    </>
  );
};

export default HistoryDownloadFeedbackModals;
